import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    BarChart3,
    RefreshCw,
    Calendar,
    FileText,
    DollarSign,
    Users,
    HelpCircle,
    Settings
} from 'lucide-react';

const Sidebar = ({ isCollapsed, onToggle }) => {
    const location = useLocation();

    // Check if a route is active
    const isActive = (path) => {
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    // Main navigation items
    const mainNavItems = [
        { path: '/dashboard', label: 'Dashboard', icon: BarChart3 },
        { path: '/pipeline', label: 'Pipeline', icon: RefreshCw },
        { path: '/appointments', label: 'Appointments', icon: Calendar },
        { path: '/quotes', label: 'Quotes', icon: FileText },
        { path: '/invoices', label: 'Invoices', icon: DollarSign },
        { path: '/clients', label: 'Clients', icon: Users },
    ];

    // Bottom navigation items
    const bottomNavItems = [
        { path: '/support', label: 'Support', icon: HelpCircle },
        { path: '/settings', label: 'Settings', icon: Settings },
    ];

    const navLinkClasses = (active) =>
        `flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} px-4 py-3 rounded-lg transition-all duration-200 ${active
            ? 'bg-primary-600 text-white shadow-md'
            : 'text-slate-600 hover:bg-slate-100 hover:text-primary-600'
        }`;

    return (
        <aside className={`hidden md:flex md:flex-col bg-white border-r border-slate-200 transition-all duration-300 self-start sticky top-0 max-h-screen ${isCollapsed ? 'w-20' : 'w-64'}`}>
            {/* Toggle Button - Small arrow on right edge */}
            <button
                onClick={onToggle}
                className="absolute top-1/2 -right-3 w-6 h-12 bg-white border border-slate-200 rounded-r-md shadow-sm hover:bg-slate-50 transition-colors duration-200 flex items-center justify-center z-10"
                style={{ transform: 'translateY(-50%)' }}
                title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
                <svg
                    className="w-3 h-3 text-slate-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    {isCollapsed ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M9 5l7 7-7 7"></path>
                    ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M15 19l-7-7 7-7"></path>
                    )}
                </svg>
            </button>

            {/* Main Navigation */}
            <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
                {mainNavItems.map((item) => {
                    const Icon = item.icon;
                    return (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={navLinkClasses(isActive(item.path))}
                            title={isCollapsed ? item.label : ''}
                        >
                            <Icon className="w-5 h-5" />
                            {!isCollapsed && <span className="font-medium">{item.label}</span>}
                        </Link>
                    );
                })}
            </nav>

            {/* Bottom Navigation */}
            <nav className="px-4 py-6 border-t border-slate-200 space-y-2">
                {bottomNavItems.map((item) => {
                    const Icon = item.icon;
                    return (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={navLinkClasses(isActive(item.path))}
                            title={isCollapsed ? item.label : ''}
                        >
                            <Icon className="w-5 h-5" />
                            {!isCollapsed && <span className="font-medium">{item.label}</span>}
                        </Link>
                    );
                })}
            </nav>
        </aside>
    );
};

export default Sidebar;
